import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  bestaetigeDauerauftrag,
  lehneVorschlagAb,
  selectBuchungen,
  selectDauerbuchungen,
  selectKategorien,
} from 'modules/finanzen/features/finanzenSlice';
import { erkenneDauerauftraege } from 'modules/finanzen/features/dauerauftragErkennung';
import { kategorieIcon } from 'modules/finanzen/features/konstanten';
import { istEinnahme } from 'modules/finanzen/features/berechnung';
import { formatKurs } from 'core/utils/format';

/**
 * Zeigt automatisch erkannte, noch NICHT entschiedene Serien (dieselbe Zahlung in
 * >= 3 aufeinanderfolgenden Monaten). „Als Dauerauftrag" speichert die Serie und
 * verknuepft passende Buchungen; „✕" lehnt den Vorschlag dauerhaft ab (landet im
 * Archiv). Beide Entscheidungen bleiben erhalten – der Vorschlag kommt nicht wieder.
 */
export default function DauerauftragVorschlaege() {
  const dispatch = useDispatch();
  const buchungen = useSelector(selectBuchungen);
  const dauerbuchungen = useSelector(selectDauerbuchungen);
  const eigeneKategorien = useSelector(selectKategorien);
  const [laeuftKey, setLaeuftKey] = useState(null);

  const vorschlaege = useMemo(
    () => erkenneDauerauftraege(buchungen, dauerbuchungen),
    [buchungen, dauerbuchungen]
  );

  if (vorschlaege.length === 0) return null;

  const entscheide = async (vorschlag, aktion) => {
    setLaeuftKey(vorschlag.key);
    try {
      await dispatch(aktion(vorschlag)).unwrap();
    } catch {
      // Bei Fehler bleibt der Vorschlag sichtbar und kann erneut entschieden werden.
    } finally {
      setLaeuftKey(null);
    }
  };

  return (
    <section className="mb-6 rounded-2xl border border-brand-600/30 bg-brand-600/5 p-4">
      <h2 className="mb-3 text-sm font-semibold text-ink">
        🔎 Erkannte Daueraufträge <span className="text-ink-muted">({vorschlaege.length})</span>
      </h2>
      <ul className="space-y-2">
        {vorschlaege.map((v) => (
          <VorschlagZeile
            key={v.key}
            vorschlag={v}
            icon={kategorieIcon(v.typ, v.kategorie, eigeneKategorien)}
            laeuft={laeuftKey === v.key}
            onBestaetigen={() => entscheide(v, bestaetigeDauerauftrag)}
            onAblehnen={() => entscheide(v, lehneVorschlagAb)}
          />
        ))}
      </ul>
    </section>
  );
}

/** Eine Vorschlagszeile mit Speichern/Ablehnen. */
function VorschlagZeile({ vorschlag, icon, laeuft, onBestaetigen, onAblehnen }) {
  const einnahme = istEinnahme(vorschlag);
  const ton = einnahme ? 'text-gain' : 'text-loss';
  return (
    <li className="flex items-center gap-3 rounded-xl border border-line bg-paper px-3 py-2">
      <span className="text-xl" aria-hidden="true">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-ink">{vorschlag.notiz || vorschlag.kategorie}</span>
        <span className="block text-xs text-ink-muted">
          monatlich · {vorschlag.anzahl}× erkannt · {vorschlag.kategorie}
        </span>
      </span>
      <span className={`shrink-0 text-sm font-semibold tabular-nums ${ton}`}>
        {einnahme ? '+' : '−'} {formatKurs(vorschlag.betrag)}
      </span>
      <button
        type="button"
        onClick={onBestaetigen}
        disabled={laeuft}
        className="shrink-0 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
      >
        {laeuft ? 'Speichere…' : 'Als Dauerauftrag'}
      </button>
      <button
        type="button"
        onClick={onAblehnen}
        disabled={laeuft}
        className="shrink-0 rounded-lg px-2 py-1 text-ink-muted transition-colors hover:bg-ink/5 disabled:opacity-50"
        aria-label="Ablehnen (ins Archiv)"
        title="Ablehnen – kommt nicht wieder, liegt im Archiv"
      >
        ✕
      </button>
    </li>
  );
}
